import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoLockup } from '../components/LogoLockup';

/* ── Design tokens ──────────────────────────────────────────────────────── */
const P = '#7c6ff7';
const P2 = '#a78bfa';
const A = '#f97316';
const G = '#22c55e';
const CYAN = '#06b6d4';
const PINK = '#ec4899';
const BG = '#050608';
const CARD = '#0c0d12';
const BORDER = 'rgba(255,255,255,0.06)';

/* ── SVG Illustrations (inline, no external deps) ─────────────────────── */

function HeroIllustration() {
  return (
    <svg viewBox="0 0 520 420" fill="none" style={{ width: '100%', maxWidth: 520, height: 'auto' }}>
      {/* Floating abstract shapes */}
      <defs>
        <linearGradient id="gp" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor={P} /><stop offset="100%" stopColor={A} /></linearGradient>
        <linearGradient id="gc" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor={CYAN} /><stop offset="100%" stopColor={P} /></linearGradient>
        <linearGradient id="gpk" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor={PINK} /><stop offset="100%" stopColor={A} /></linearGradient>
        <filter id="glow"><feGaussianBlur stdDeviation="8" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        <filter id="glow2"><feGaussianBlur stdDeviation="16" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>

      {/* Background grid dots */}
      {Array.from({ length: 12 }).map((_, r) => Array.from({ length: 14 }).map((_, c) => (
        <circle key={`d${r}${c}`} cx={40 * c + 20} cy={40 * r + 20} r="1" fill="rgba(255,255,255,0.06)" />
      )))}

      {/* Glowing orbs */}
      <circle cx="120" cy="90" r="80" fill="url(#gp)" opacity="0.06" filter="url(#glow2)">
        <animate attributeName="r" values="80;90;80" dur="6s" repeatCount="indefinite" />
      </circle>
      <circle cx="400" cy="340" r="60" fill="url(#gc)" opacity="0.06" filter="url(#glow2)">
        <animate attributeName="r" values="60;70;60" dur="5s" repeatCount="indefinite" />
      </circle>

      {/* Main "dashboard" card — Reality Check output mockup */}
      <g transform="translate(60, 40)">
        <rect x="0" y="0" width="400" height="320" rx="20" fill="#0f1017" stroke="rgba(124,111,247,0.2)" strokeWidth="1" />
        {/* Title bar */}
        <rect x="0" y="0" width="400" height="48" rx="20" fill="#0f1017" />
        <rect x="0" y="28" width="400" height="20" fill="#0f1017" />
        <line x1="0" y1="48" x2="400" y2="48" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        <circle cx="24" cy="24" r="5" fill="#ef4444" opacity="0.7" />
        <circle cx="42" cy="24" r="5" fill="#eab308" opacity="0.7" />
        <circle cx="60" cy="24" r="5" fill="#22c55e" opacity="0.7" />
        <text x="200" y="29" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="11" fontFamily="Inter, system-ui">StartupScore — Pipeline</text>

        {/* PREVIEW badge */}
        <rect x="318" y="58" width="66" height="20" rx="10" fill="rgba(124,111,247,0.12)" stroke="rgba(124,111,247,0.25)" strokeWidth="0.5" />
        <text x="351" y="72" textAnchor="middle" fill="rgba(124,111,247,0.6)" fontSize="8" fontWeight="700" fontFamily="Inter, system-ui" letterSpacing="1">PREVIEW</text>

        {/* Section label */}
        <text x="24" y="76" fill="rgba(255,255,255,0.25)" fontSize="9" fontWeight="700" fontFamily="Inter, system-ui" letterSpacing="1.5">REALITY CHECK OUTPUT</text>

        {/* Score display */}
        <text x="24" y="118" fill={P} fontSize="42" fontWeight="900" fontFamily="Inter, system-ui">74</text>
        <text x="112" y="118" fill="rgba(255,255,255,0.2)" fontSize="20" fontWeight="600" fontFamily="Inter, system-ui">/ 100</text>

        {/* Divider */}
        <line x1="24" y1="134" x2="376" y2="134" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

        {/* Data row 1: Market Size */}
        <text x="24" y="162" fill="rgba(255,255,255,0.4)" fontSize="13" fontFamily="Inter, system-ui">Market Size</text>
        <rect x="220" y="148" width="156" height="22" rx="6" fill="rgba(34,197,94,0.08)" stroke="rgba(34,197,94,0.2)" strokeWidth="0.5" />
        <text x="298" y="163" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="600" fontFamily="Inter, system-ui">Large ($4.2B TAM)</text>

        {/* Data row 2: Competition */}
        <text x="24" y="198" fill="rgba(255,255,255,0.4)" fontSize="13" fontFamily="Inter, system-ui">Competition</text>
        <rect x="220" y="184" width="90" height="22" rx="6" fill="rgba(234,179,8,0.08)" stroke="rgba(234,179,8,0.2)" strokeWidth="0.5" />
        <text x="265" y="199" textAnchor="middle" fill="#eab308" fontSize="11" fontWeight="600" fontFamily="Inter, system-ui">Moderate</text>

        {/* Data row 3: Verdict */}
        <text x="24" y="234" fill="rgba(255,255,255,0.4)" fontSize="13" fontFamily="Inter, system-ui">Verdict</text>
        <rect x="220" y="220" width="80" height="22" rx="6" fill="rgba(34,197,94,0.1)" stroke="rgba(34,197,94,0.25)" strokeWidth="0.5" />
        <text x="260" y="235" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="700" fontFamily="Inter, system-ui">✓ Build</text>

        {/* Divider */}
        <line x1="24" y1="254" x2="376" y2="254" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

        {/* Pipeline steps mini */}
        <g transform="translate(24, 268)">
          {['Profile', 'Idea', 'Reality', 'Position', 'Plan'].map((label, i) => (
            <g key={i} transform={`translate(${i * 42}, 0)`}>
              <circle cx="8" cy="8" r="8" fill={i < 3 ? P : 'rgba(255,255,255,0.08)'} opacity={i < 3 ? 1 : 0.5} />
              {i < 3 && <path d="M4 8 L7 11 L12 5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />}
              {i === 3 && <circle cx="8" cy="8" r="3" fill="rgba(255,255,255,0.3)" />}
              <text x="8" y="28" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="6" fontFamily="Inter, system-ui">{label}</text>
              {i < 4 && <line x1="16" y1="8" x2="42" y2="8" stroke="rgba(124,111,247,0.15)" strokeWidth="1" />}
            </g>
          ))}
        </g>

        {/* Bottom muted text */}
        <text x="24" y="312" fill="rgba(255,255,255,0.15)" fontSize="9" fontFamily="Inter, system-ui">Generated in ~8 min · Live web data</text>
      </g>

      {/* Floating elements around the card */}
      {/* Rocket */}
      <g transform="translate(30, 180)" filter="url(#glow)">
        <circle cx="20" cy="20" r="20" fill="rgba(124,111,247,0.1)" />
        <text x="20" y="26" textAnchor="middle" fontSize="18">🚀</text>
        <animateTransform attributeName="transform" type="translate" values="30,180;30,170;30,180" dur="3s" repeatCount="indefinite" />
      </g>

      {/* Lightning bolt */}
      <g transform="translate(470, 130)" filter="url(#glow)">
        <circle cx="16" cy="16" r="16" fill="rgba(249,115,22,0.1)" />
        <text x="16" y="22" textAnchor="middle" fontSize="16">⚡</text>
        <animateTransform attributeName="transform" type="translate" values="470,130;470,120;470,130" dur="4s" repeatCount="indefinite" />
      </g>

      {/* Chart icon */}
      <g transform="translate(460, 280)" filter="url(#glow)">
        <circle cx="16" cy="16" r="16" fill="rgba(6,182,212,0.1)" />
        <text x="16" y="22" textAnchor="middle" fontSize="16">📊</text>
        <animateTransform attributeName="transform" type="translate" values="460,280;460,270;460,280" dur="3.5s" repeatCount="indefinite" />
      </g>

      {/* Target */}
      <g transform="translate(20, 320)" filter="url(#glow)">
        <circle cx="16" cy="16" r="16" fill="rgba(236,72,153,0.1)" />
        <text x="16" y="22" textAnchor="middle" fontSize="16">🎯</text>
        <animateTransform attributeName="transform" type="translate" values="20,320;20,310;20,320" dur="4.5s" repeatCount="indefinite" />
      </g>

      {/* Connecting lines (constellation style) */}
      <line x1="50" y1="200" x2="60" y2="160" stroke="rgba(124,111,247,0.1)" strokeWidth="1" strokeDasharray="4 4">
        <animate attributeName="opacity" values="0.1;0.3;0.1" dur="3s" repeatCount="indefinite" />
      </line>
      <line x1="486" y1="146" x2="460" y2="120" stroke="rgba(249,115,22,0.1)" strokeWidth="1" strokeDasharray="4 4">
        <animate attributeName="opacity" values="0.1;0.3;0.1" dur="4s" repeatCount="indefinite" />
      </line>
      <line x1="36" y1="336" x2="60" y2="320" stroke="rgba(236,72,153,0.1)" strokeWidth="1" strokeDasharray="4 4">
        <animate attributeName="opacity" values="0.1;0.3;0.1" dur="3.5s" repeatCount="indefinite" />
      </line>

      {/* Particle dots */}
      {[
        { x: 100, y: 30, d: '4s' }, { x: 350, y: 50, d: '3s' }, { x: 480, y: 200, d: '5s' },
        { x: 30, y: 400, d: '3.5s' }, { x: 500, y: 400, d: '4.5s' }, { x: 250, y: 420, d: '3s' },
      ].map((p, i) => (
        <circle key={`p${i}`} cx={p.x} cy={p.y} r="2" fill={P} opacity="0.3">
          <animate attributeName="opacity" values="0.1;0.5;0.1" dur={p.d} repeatCount="indefinite" />
        </circle>
      ))}
    </svg>
  );
}

/* ── Decorative SVG sketches for each roadmap step ─────────────────────── */
function SketchProfile({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 80 80" fill="none" style={{ width: 80, height: 80 }}>
      <circle cx="40" cy="28" r="12" stroke={color} strokeWidth="1.5" fill="none" opacity="0.6" />
      <path d="M20 64 Q28 44 40 44 Q52 44 60 64" stroke={color} strokeWidth="1.5" fill="none" opacity="0.4" />
      <circle cx="40" cy="40" r="36" stroke={color} strokeWidth="1" fill="none" strokeDasharray="4 6" opacity="0.15" />
      <line x1="62" y1="14" x2="70" y2="8" stroke={color} strokeWidth="1" opacity="0.3" />
      <circle cx="72" cy="6" r="3" fill={color} opacity="0.2" />
      <line x1="12" y1="58" x2="6" y2="64" stroke={color} strokeWidth="1" opacity="0.3" />
      <circle cx="4" cy="66" r="2" fill={color} opacity="0.2" />
    </svg>
  );
}
function SketchLightbulb({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 80 80" fill="none" style={{ width: 80, height: 80 }}>
      <path d="M40 12 Q56 12 56 32 Q56 44 46 48 L46 56 L34 56 L34 48 Q24 44 24 32 Q24 12 40 12Z" stroke={color} strokeWidth="1.5" fill="none" opacity="0.5" />
      <line x1="34" y1="60" x2="46" y2="60" stroke={color} strokeWidth="1.5" opacity="0.4" />
      <line x1="36" y1="64" x2="44" y2="64" stroke={color} strokeWidth="1.5" opacity="0.3" />
      {/* rays */}
      <line x1="40" y1="2" x2="40" y2="8" stroke={color} strokeWidth="1" opacity="0.3" />
      <line x1="60" y1="8" x2="56" y2="14" stroke={color} strokeWidth="1" opacity="0.3" />
      <line x1="20" y1="8" x2="24" y2="14" stroke={color} strokeWidth="1" opacity="0.3" />
      <line x1="66" y1="28" x2="60" y2="28" stroke={color} strokeWidth="1" opacity="0.3" />
      <line x1="14" y1="28" x2="20" y2="28" stroke={color} strokeWidth="1" opacity="0.3" />
      <circle cx="40" cy="32" r="4" fill={color} opacity="0.1" />
    </svg>
  );
}
function SketchMagnifier({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 80 80" fill="none" style={{ width: 80, height: 80 }}>
      <circle cx="34" cy="32" r="18" stroke={color} strokeWidth="2" fill="none" opacity="0.5" />
      <line x1="47" y1="45" x2="64" y2="62" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.4" />
      {/* inner details */}
      <circle cx="34" cy="32" r="10" stroke={color} strokeWidth="0.8" fill="none" strokeDasharray="3 4" opacity="0.2" />
      <path d="M28 26 Q34 20 40 26" stroke={color} strokeWidth="1" fill="none" opacity="0.3" />
      {/* sparkles */}
      <circle cx="60" cy="16" r="2" fill={color} opacity="0.25" />
      <line x1="58" y1="16" x2="62" y2="16" stroke={color} strokeWidth="0.8" opacity="0.3" />
      <line x1="60" y1="14" x2="60" y2="18" stroke={color} strokeWidth="0.8" opacity="0.3" />
      <circle cx="14" cy="56" r="1.5" fill={color} opacity="0.2" />
    </svg>
  );
}
function SketchTarget({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 80 80" fill="none" style={{ width: 80, height: 80 }}>
      <circle cx="40" cy="40" r="28" stroke={color} strokeWidth="1.5" fill="none" opacity="0.3" />
      <circle cx="40" cy="40" r="18" stroke={color} strokeWidth="1.5" fill="none" opacity="0.4" />
      <circle cx="40" cy="40" r="8" stroke={color} strokeWidth="1.5" fill="none" opacity="0.5" />
      <circle cx="40" cy="40" r="3" fill={color} opacity="0.5" />
      {/* arrow */}
      <line x1="58" y1="16" x2="44" y2="36" stroke={color} strokeWidth="1.5" opacity="0.4" />
      <path d="M58 16 L52 18 L56 22 Z" fill={color} opacity="0.3" />
      {/* decorative lines */}
      <line x1="4" y1="40" x2="12" y2="40" stroke={color} strokeWidth="0.8" opacity="0.2" />
      <line x1="68" y1="40" x2="76" y2="40" stroke={color} strokeWidth="0.8" opacity="0.2" />
      <line x1="40" y1="4" x2="40" y2="12" stroke={color} strokeWidth="0.8" opacity="0.2" />
    </svg>
  );
}
function SketchRocket({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 80 80" fill="none" style={{ width: 80, height: 80 }}>
      <path d="M40 8 Q52 20 52 40 L44 52 L36 52 L28 40 Q28 20 40 8Z" stroke={color} strokeWidth="1.5" fill="none" opacity="0.5" />
      <circle cx="40" cy="30" r="5" stroke={color} strokeWidth="1" fill="none" opacity="0.4" />
      {/* fins */}
      <path d="M28 40 Q20 44 22 52 L30 48" stroke={color} strokeWidth="1" fill="none" opacity="0.35" />
      <path d="M52 40 Q60 44 58 52 L50 48" stroke={color} strokeWidth="1" fill="none" opacity="0.35" />
      {/* flame */}
      <path d="M36 52 Q38 62 40 66 Q42 62 44 52" stroke={color} strokeWidth="1.2" fill={color} opacity="0.2" />
      <path d="M38 52 Q39 58 40 60 Q41 58 42 52" fill={color} opacity="0.15" />
      {/* stars */}
      <circle cx="14" cy="14" r="1.5" fill={color} opacity="0.3" />
      <circle cx="66" cy="10" r="1" fill={color} opacity="0.2" />
      <circle cx="10" cy="50" r="1" fill={color} opacity="0.2" />
      <circle cx="70" cy="56" r="1.5" fill={color} opacity="0.25" />
    </svg>
  );
}

/* ── Roadmap step data ─────────────────────────────────────────────────── */
const ROADMAP_STEPS = [
  {
    num: '01', title: 'Founder Profile', time: '~3 min',
    color: P, gradient: `linear-gradient(135deg, ${P}, ${P2})`,
    desc: 'Tell AI about yourself — your technical skills, available budget, time commitment, and past experience. Every insight from here on is tailored to what you can actually execute.',
    outputs: ['Founder archetype', 'Skill map', 'Resource assessment'],
    Sketch: SketchProfile,
  },
  {
    num: '02', title: 'Idea Analysis', time: '~5 min',
    color: CYAN, gradient: `linear-gradient(135deg, ${CYAN}, ${P})`,
    desc: 'Describe your startup idea in plain English. AI structures it into a problem-solution framework, scores your clarity out of 100, and identifies gaps in your thinking before you go further.',
    outputs: ['Clarity score', 'Problem-solution fit', 'Structured breakdown'],
    Sketch: SketchLightbulb,
  },
  {
    num: '03', title: 'Reality Check', time: '~8 min',
    color: PINK, gradient: `linear-gradient(135deg, ${P}, ${PINK})`,
    highlight: true,
    desc: 'The deep dive. AI searches the live web to score your market size, existing competition, timing, monetization potential, and execution feasibility. You get a composite score and a clear Build / Pivot / Kill recommendation.',
    outputs: ['Market score', 'Competition map', 'Go/No-go verdict', 'Live web data'],
    Sketch: SketchMagnifier,
  },
  {
    num: '04', title: 'Strategic Positioning', time: '~6 min',
    color: A, gradient: `linear-gradient(135deg, ${A}, #fbbf24)`,
    desc: 'Choose your niche, differentiation angle, and revenue model from AI-generated options. Walk away with a polished elevator pitch, ideal customer persona, and competitive moat strategy.',
    outputs: ['Elevator pitch', 'ICP persona', 'Monetization plan'],
    Sketch: SketchTarget,
  },
  {
    num: '05', title: 'Validation Plan', time: '~5 min',
    color: G, gradient: `linear-gradient(135deg, ${G}, ${CYAN})`,
    desc: 'Get a 14-day validation experiment designed for your specific idea — complete with landing page headlines, outreach email scripts, survey questions, and quantified success metrics so you know exactly when to commit.',
    outputs: ['14-day roadmap', 'Landing copy', 'Outreach scripts', 'Success metrics'],
    Sketch: SketchRocket,
  },
];

function AbstractShapeBg() {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {/* Grid */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '40px 40px', opacity: 0.03 }} />
      {/* Large gradient orbs */}
      <div style={{ position: 'absolute', width: 1000, height: 1000, borderRadius: '50%', top: -400, left: -300, background: `radial-gradient(circle, rgba(124,111,247,0.08) 0%, transparent 50%)` }} />
      <div style={{ position: 'absolute', width: 800, height: 800, borderRadius: '50%', top: -200, right: -200, background: `radial-gradient(circle, rgba(249,115,22,0.05) 0%, transparent 50%)` }} />
      <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', bottom: -200, left: '30%', background: `radial-gradient(circle, rgba(6,182,212,0.04) 0%, transparent 50%)` }} />
      {/* Noise texture overlay */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.02, backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />
    </div>
  );
}

/* ── Hook: IntersectionObserver ─────────────────────────────────────────── */
function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* ── Sub-components ────────────────────────────────────────────────────── */

function SectionLabel({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <p style={{
      textAlign: 'center', fontSize: 11, fontWeight: 700, color: color || P,
      letterSpacing: 3, textTransform: 'uppercase', marginBottom: 16,
    }}>{children}</p>
  );
}

function SectionTitle({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <>
      <h2 style={{
        textAlign: 'center', fontSize: 'clamp(28px, 4vw, 52px)', fontWeight: 900,
        color: '#fff', letterSpacing: -2, lineHeight: 1.08, marginBottom: sub ? 18 : 56,
      }}>{children}</h2>
      {sub && <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.38)', fontSize: 17, lineHeight: 1.65, marginBottom: 60, maxWidth: 580, marginLeft: 'auto', marginRight: 'auto' }}>{sub}</p>}
    </>
  );
}

function Btn({ children, onClick, variant = 'primary', size = 'md', pulse, className, style: extraStyle }: {
  children: React.ReactNode; onClick: () => void;
  variant?: 'primary' | 'ghost'; size?: 'sm' | 'md' | 'lg'; pulse?: boolean; className?: string;
  style?: React.CSSProperties;
}) {
  const base: React.CSSProperties = {
    border: variant === 'ghost' ? '1px solid rgba(255,255,255,0.1)' : 'none',
    borderRadius: size === 'lg' ? 16 : 12,
    color: '#fff', cursor: 'pointer', fontWeight: 700, transition: 'all 0.25s',
    fontSize: size === 'lg' ? 18 : size === 'sm' ? 13 : 15,
    padding: size === 'lg' ? '20px 48px' : size === 'sm' ? '10px 20px' : '14px 32px',
    background: variant === 'primary' ? `linear-gradient(135deg, ${P}, ${A})` : 'rgba(255,255,255,0.03)',
    ...(pulse ? { animation: 'ctaPulse 2.8s ease-in-out infinite' } : {}),
    ...extraStyle,
  };
  return <button onClick={onClick} className={className} style={base}>{children}</button>;
}

function FAQItem({ q, a, isOpen, onClick }: { q: string; a: string; isOpen: boolean; onClick: () => void }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => { if (contentRef.current) setHeight(isOpen ? contentRef.current.scrollHeight : 0); }, [isOpen]);
  return (
    <div style={{ borderBottom: `1px solid ${BORDER}` }}>
      <button onClick={onClick} style={{
        width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '22px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
      }}>
        <span style={{ color: '#fff', fontSize: 16, fontWeight: 600, paddingRight: 16 }}>{q}</span>
        <span style={{ color: P, fontSize: 22, flexShrink: 0, transition: 'transform 0.3s', transform: isOpen ? 'rotate(45deg)' : 'none', fontWeight: 300 }}>+</span>
      </button>
      <div style={{ height, overflow: 'hidden', transition: 'height 0.3s ease' }}>
        <div ref={contentRef} style={{ paddingBottom: 22 }}>
          <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: 15, lineHeight: 1.7 }}>{a}</p>
        </div>
      </div>
    </div>
  );
}

/* ── Stat counter with animation ──────────────────────────────────────── */
function AnimatedNumber({ value, suffix, label, staticText }: { value: number; suffix: string; label: string; staticText?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [display, setDisplay] = useState(0);
  const hasAnimated = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || hasAnimated.current) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        hasAnimated.current = true;
        if (staticText) {
          // No counting animation for static text stats
          io.disconnect();
          return;
        }
        setDisplay(0);
        let start = 0;
        const step = () => {
          start += Math.ceil(value / 30);
          if (start >= value) { setDisplay(value); return; }
          setDisplay(start);
          requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        io.disconnect();
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [value, staticText]);
  return (
    <div ref={ref} style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 900, background: `linear-gradient(135deg, ${P}, ${A})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1.1 }}>
        {staticText || `${display}${suffix}`}
      </div>
      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', marginTop: 6, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

/* ── Main component ────────────────────────────────────────────────────── */

export default function LandingPage() {
  const navigate = useNavigate();
  const go = useCallback((path: string) => navigate(path), [navigate]);

  useEffect(() => { if (localStorage.getItem('sf_token')) navigate('/startups', { replace: true }); }, [navigate]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap';
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, []);

  const scrollTo = useCallback((id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), []);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const roadmapAnim = useReveal(0.08);
  const afterAnim = useReveal();
  const intelAnim = useReveal();
  const comparisonAnim = useReveal();
  const pricingAnim = useReveal();
  const faqAnim = useReveal();
  const ctaAnim = useReveal();

  const [scrollPct, setScrollPct] = useState(0);
  useEffect(() => {
    const h = () => { const el = document.documentElement; setScrollPct(el.scrollTop / (el.scrollHeight - el.clientHeight)); };
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const PACKS = [
    { id: 'STARTER', credits: 100, price: 9.90,  betaPrice: 4.95,  label: 'Starter', tag: 'Get started', hint: '~6 full pipelines + intel modules' },
    { id: 'GROWTH',  credits: 250, price: 19.90, betaPrice: 9.95,  label: 'Growth',  tag: 'Most popular', hint: '~15 pipelines + full intel suite', best: true },
    { id: 'PRO',     credits: 500, price: 29.90, betaPrice: 14.95, label: 'Pro',     tag: 'Best value', hint: 'Unlimited validation for months' },
  ];

  const AFTER_FEATURES = [
    { icon: '🏗', title: 'MVP Blueprint', desc: 'Personalized tech stack, feature priority matrix, budget estimate, and a step-by-step build plan.', color: P, badge: 'Requires credits', badgeColor: '#d97706' },
    { icon: '📊', title: 'Financial Modeler', desc: 'Multi-scenario projections with breakeven analysis, unit economics, and sensitivity charts.', color: CYAN, badge: 'Requires credits', badgeColor: '#d97706' },
    { icon: '📈', title: 'Startup Dashboard', desc: 'Track validation progress, log signals, manage milestones, and see your startup stage evolve.', color: A, badge: 'Included free', badgeColor: '#16a34a' },
    { icon: '🔗', title: 'Shareable Reports', desc: 'Generate investor-ready links to your validation data. Share progress with co-founders and advisors.', color: G, badge: 'Included free', badgeColor: '#16a34a' },
  ];

  const INTEL_MODULES = [
    { icon: '🏷', title: 'Name Validation', desc: 'Trademark risk, domain availability, and competitor overlap.', color: P },
    { icon: '🔑', title: 'Keyword Intel', desc: 'High-value keywords, content calendar, and SEO strategy.', color: CYAN },
    { icon: '⚔️', title: 'Competitor Intel', desc: 'Deep profiles with pricing, strengths, and battle cards.', color: PINK },
    { icon: '📈', title: 'Trends & Timing', desc: 'Market growth, trending topics, and investor signals.', color: A },
    { icon: '💬', title: 'Sentiment', desc: 'Customer pain points, emotional triggers, and copy templates.', color: G },
  ];

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: BG, overflowX: 'hidden', color: '#fff' }}>
      <style>{`
        html{scroll-behavior:smooth}
        *{box-sizing:border-box}
        @keyframes fadeUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes ctaPulse{0%,100%{box-shadow:0 8px 40px rgba(124,111,247,0.3)}50%{box-shadow:0 8px 60px rgba(124,111,247,0.55)}}
        @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
        @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
        @keyframes gradientShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
        @keyframes borderGlow{0%,100%{border-color:rgba(124,111,247,0.15)}50%{border-color:rgba(124,111,247,0.35)}}
        @keyframes rotate{from{transform:rotate(0)}to{transform:rotate(360deg)}}
        @keyframes travelDown{0%{top:-40px;opacity:0}20%{opacity:1}80%{opacity:1}100%{top:calc(100% + 40px);opacity:0}}
        @keyframes pulseRing{0%{transform:scale(1);opacity:0.3}50%{transform:scale(1.4);opacity:0}100%{transform:scale(1);opacity:0}}
        .reveal{opacity:0;transform:translateY(24px);transition:opacity 0.7s ease,transform 0.7s ease}
        .reveal.visible{opacity:1;transform:none}
        .hover-lift{transition:transform 0.3s cubic-bezier(.4,0,.2,1),box-shadow 0.3s}
        .hover-lift:hover{transform:translateY(-6px)!important;box-shadow:0 20px 60px rgba(0,0,0,0.35)!important}
        .hover-border:hover{border-color:rgba(124,111,247,0.3)!important}
        .hover-glow:hover{box-shadow:0 0 40px rgba(124,111,247,0.15)!important}
        .cta-hover:hover{transform:scale(1.04)!important;filter:brightness(1.1)}
        .ghost-hover:hover{background:rgba(255,255,255,0.06)!important;border-color:rgba(255,255,255,0.15)!important}
        button{cursor:pointer}
        .glass{background:rgba(12,13,18,0.7);backdrop-filter:blur(24px);-webkit-backdrop-filter:blur(24px)}
        @media(max-width:768px){
          .hero-grid{flex-direction:column!important;text-align:center!important}
          .hero-illust{max-width:320px!important;margin:0 auto!important}
          .grid-2{grid-template-columns:1fr!important}
          .grid-3{grid-template-columns:1fr!important}
          .grid-4{grid-template-columns:1fr 1fr!important}
          .grid-5{grid-template-columns:1fr 1fr!important}
        }
        @media(max-width:480px){
          .grid-4{grid-template-columns:1fr!important}
          .grid-5{grid-template-columns:1fr!important
          .nav-links{display:none!important}
          .trust-bar{gap:12px!important}
          .trust-item{font-size:10px!important}
          .footer-grid{grid-template-columns:1fr!important;text-align:center!important;gap:24px!important}
          .mobile-cta{display:flex!important}
          .hero-sub{font-size:15px!important;margin-bottom:28px!important}
          .stats-row{flex-direction:column!important;gap:28px!important}
          .roadmap-row{flex-direction:column!important;gap:16px!important}
          .roadmap-card{max-width:100%!important}
          .roadmap-sketch{display:none!important}
          .roadmap-line{left:26px!important}
          .hero-badge{justify-content:center!important;display:flex!important;text-align:center!important}
        }
        @media(prefers-reduced-motion:reduce){*{animation-duration:0.01ms!important;transition-duration:0.01ms!important}}
      `}</style>

      {/* ─ Scroll progress ─ */}
      <div style={{ position: 'fixed', top: 0, left: 0, height: 2, zIndex: 9999, width: `${Math.min(1, Math.max(0, scrollPct)) * 100}%`, background: `linear-gradient(90deg, ${P}, ${A})`, pointerEvents: 'none' }} />

      {/* ─ Navbar ─ */}
      <nav className="glass" style={{
        position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 998,
        border: `1px solid ${BORDER}`, borderRadius: 100,
        padding: '8px 8px 8px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20,
        width: 'min(880px, calc(100vw - 24px))',
      }}>
        <LogoLockup size="md" />
        <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          {['Pipeline', 'Features', 'Pricing', 'FAQ'].map(label => (
            <button key={label} onClick={() => scrollTo(label.toLowerCase())} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 13, fontWeight: 500,
              transition: 'color 0.2s',
            }}
              onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
            >{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => go('/auth')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 13, padding: '8px 14px' }}>Sign in</button>
          <Btn onClick={() => go('/auth?mode=signup')} size="sm" className="cta-hover">Start Free</Btn>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════
          HERO
      ═══════════════════════════════════════════════════ */}
      <section style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '120px 24px 60px', position: 'relative', overflow: 'hidden',
      }}>
        <AbstractShapeBg />

        <div style={{ maxWidth: 1200, width: '100%', position: 'relative', zIndex: 1 }}>
          <div className="hero-grid" style={{ display: 'flex', alignItems: 'center', gap: 48 }}>
            {/* Left: Copy */}
            <div style={{ flex: '1 1 520px', maxWidth: 600 }}>
              <div className="hero-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 28, animation: 'fadeIn 0.6s ease both', background: 'rgba(124,111,247,0.06)', border: '1px solid rgba(124,111,247,0.15)', borderRadius: 100, padding: '6px 16px 6px 10px' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: G, display: 'inline-block', boxShadow: `0 0 8px ${G}` }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>25 free credits (= 1 full pipeline + extras) — no credit card needed</span>
              </div>

              <h1 style={{
                fontSize: 'clamp(38px, 5.5vw, 72px)', fontWeight: 900, color: '#fff',
                lineHeight: 1.04, letterSpacing: -3, marginBottom: 22,
                animation: 'fadeUp 0.7s ease 0.1s both',
              }}>
                Validate your<br />
                startup idea<br />
                <span style={{
                  background: `linear-gradient(135deg, ${P}, ${CYAN}, ${A})`,
                  backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}>before you build.</span>
              </h1>

              <p className="hero-sub" style={{
                fontSize: 18, color: 'rgba(255,255,255,0.42)', lineHeight: 1.7, marginBottom: 40, maxWidth: 500,
                animation: 'fadeUp 0.7s ease 0.25s both',
              }}>
                A 5-step AI pipeline that analyzes your market, scores your idea,<br />
                builds your positioning, and hands you a 14-day validation plan — in under 30 minutes.
              </p>

              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', animation: 'fadeUp 0.7s ease 0.4s both' }}>
                <Btn onClick={() => go('/auth?mode=signup')} size="lg" pulse className="cta-hover">
                  Start My Pipeline — Free
                </Btn>
                <Btn onClick={() => scrollTo('pipeline')} variant="ghost" size="lg" className="ghost-hover">
                  See how it works ↓
                </Btn>
              </div>

              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', marginTop: 18, animation: 'fadeUp 0.7s ease 0.55s both' }}>
                5 AI-powered steps · Personalized to you · Share results with investors
              </p>
            </div>

            {/* Right: Hero illustration */}
            <div className="hero-illust" style={{ flexShrink: 0, maxWidth: 520, animation: 'fadeUp 0.9s ease 0.3s both' }}>
              <HeroIllustration />
            </div>
          </div>
        </div>
      </section>

      {/* ─ Stats bar ─ */}
      <div style={{
        borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`,
        padding: '48px 24px', background: 'rgba(12,13,18,0.5)',
      }}>
        <div className="stats-row" style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-around', gap: 40 }}>
          <AnimatedNumber value={5} suffix="" label="AI-Powered Steps" />
          <AnimatedNumber value={27} suffix="min" label="Average Completion" />
          <AnimatedNumber value={5} suffix="" label="Intelligence Modules" />
          <AnimatedNumber value={0} suffix="" label="First Pipeline" staticText="Free" />
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════
          THE ROADMAP FUNNEL
      ═══════════════════════════════════════════════════ */}
      <section id="pipeline" ref={roadmapAnim.ref} style={{ padding: '120px 24px 80px', position: 'relative' }}>
        {/* Background effects */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', width: 800, height: 800, borderRadius: '50%', top: '5%', left: '50%', transform: 'translateX(-50%)', background: `radial-gradient(circle, rgba(124,111,247,0.04) 0%, transparent 60%)` }} />
          <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', bottom: '10%', right: '-5%', background: `radial-gradient(circle, rgba(6,182,212,0.03) 0%, transparent 60%)` }} />
        </div>

        <div className={`reveal ${roadmapAnim.visible ? 'visible' : ''}`} style={{ maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <SectionLabel>The Validation Roadmap</SectionLabel>
          <SectionTitle sub="Five AI-powered steps, each building on the last. Every insight is personalized to your skills, market, and idea.">
            From raw idea to launch-ready<br />in under 30 minutes.
          </SectionTitle>

          {/* Zigzag Roadmap */}
          <div style={{ position: 'relative' }}>
            {/* Vertical connecting line */}
            <div className="roadmap-line" style={{
              position: 'absolute', left: '50%', top: 40, bottom: 40, width: 2,
              background: `linear-gradient(to bottom, ${P}40, ${CYAN}40, ${PINK}40, ${A}40, ${G}40)`,
            }}>
              {/* Animated pulse traveling down */}
              <div style={{
                position: 'absolute', left: -3, width: 8, height: 40, borderRadius: 4,
                background: `linear-gradient(to bottom, transparent, ${P}, transparent)`,
                animation: 'travelDown 3s ease-in-out infinite',
              }} />
            </div>

            {ROADMAP_STEPS.map((step, i) => {
              const isLeft = i % 2 === 0;
              const { Sketch } = step;
              return (
                <div key={step.num} className="roadmap-row" style={{
                  display: 'flex', alignItems: 'center', gap: 0,
                  flexDirection: isLeft ? 'row' : 'row-reverse',
                  marginBottom: i < ROADMAP_STEPS.length - 1 ? 24 : 0,
                  position: 'relative',
                }}>
                  {/* Content card */}
                  <div className="roadmap-card" style={{ flex: 1, maxWidth: 'calc(50% - 40px)' }}>
                    <div className="hover-lift hover-border" style={{
                      background: CARD, borderRadius: 22, border: `1px solid ${BORDER}`,
                      padding: step.highlight ? '32px 28px' : '28px 24px',
                      position: 'relative', overflow: 'hidden',
                      ...(step.highlight ? { border: `1px solid ${step.color}25` } : {}),
                    }}>
                      {/* Accent corner glow */}
                      <div style={{
                        position: 'absolute', top: -40, [isLeft ? 'right' : 'left']: -40,
                        width: 120, height: 120, borderRadius: '50%',
                        background: `radial-gradient(circle, ${step.color}12, transparent 70%)`,
                        pointerEvents: 'none',
                      }} />

                      {/* Header row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, position: 'relative' }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 10,
                          background: `${step.color}12`, border: `1px solid ${step.color}30`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 13, fontWeight: 900, color: step.color, flexShrink: 0,
                        }}>{step.num}</div>
                        <div>
                          <h3 style={{ fontSize: step.highlight ? 19 : 17, fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>{step.title}</h3>
                          <span style={{ fontSize: 11, color: step.color, fontWeight: 600 }}>{step.time}</span>
                        </div>
                        {step.highlight && (
                          <span style={{
                            marginLeft: 'auto', fontSize: 9, fontWeight: 700, color: step.color,
                            background: `${step.color}10`, border: `1px solid ${step.color}25`,
                            borderRadius: 20, padding: '3px 10px', letterSpacing: 0.5, textTransform: 'uppercase',
                          }}>Core Step</span>
                        )}
                      </div>

                      {/* Description */}
                      <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, marginBottom: 16, position: 'relative' }}>
                        {step.desc}
                      </p>

                      {/* Output tags */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, position: 'relative' }}>
                        {step.outputs.map(tag => (
                          <span key={tag} style={{
                            fontSize: 11, fontWeight: 600, color: step.color, padding: '4px 10px',
                            background: `${step.color}08`, border: `1px solid ${step.color}18`,
                            borderRadius: 8, whiteSpace: 'nowrap',
                          }}>{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Center node */}
                  <div style={{
                    width: 80, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', zIndex: 2,
                  }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: '50%',
                      background: CARD, border: `2.5px solid ${step.color}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: `0 0 24px ${step.color}20, 0 0 48px ${step.color}08`,
                      position: 'relative',
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', background: step.gradient,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, fontWeight: 900, color: '#fff',
                      }}>{step.num}</div>
                    </div>
                    {/* Pulsing ring */}
                    <div style={{
                      position: 'absolute', width: 68, height: 68, borderRadius: '50%',
                      border: `1px solid ${step.color}`,
                      animation: `pulseRing 3s ease-in-out ${i * 0.4}s infinite`,
                      opacity: 0,
                    }} />
                  </div>

                  {/* Decorative sketch */}
                  <div className="roadmap-sketch" style={{
                    flex: 1, maxWidth: 'calc(50% - 40px)',
                    display: 'flex', alignItems: isLeft ? 'flex-start' : 'flex-end',
                    justifyContent: 'center', paddingTop: 8,
                  }}>
                    <div style={{ opacity: 0.7, animation: `float ${3 + i * 0.5}s ease-in-out infinite` }}>
                      <Sketch color={step.color} />
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Finish badge at bottom */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32, position: 'relative', zIndex: 2 }}>
              <div style={{
                background: `linear-gradient(135deg, ${G}12, ${CYAN}08)`, border: `1px solid ${G}30`,
                borderRadius: 100, padding: '10px 28px', display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: G, boxShadow: `0 0 8px ${G}` }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: G }}>Pipeline Complete</span>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>— ~27 min total</span>
              </div>
            </div>
          </div>

          {/* What you get callout */}
          <div style={{
            background: CARD, borderRadius: 22, border: `1px solid ${BORDER}`,
            padding: '32px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20,
            maxWidth: 740, margin: '48px auto 0',
          }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', marginBottom: 8 }}>What you walk away with</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {['Scored & validated idea', 'Market data', 'Competitive positioning', 'Elevator pitch', '14-day action plan'].map(t => (
                  <span key={t} style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '4px 12px' }}>{t}</span>
                ))}
              </div>
            </div>
            <Btn onClick={() => go('/auth?mode=signup')} className="cta-hover" pulse>Start My Pipeline</Btn>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          AFTER PIPELINE — Dashboard & Tools
      ═══════════════════════════════════════════════════ */}
      <section id="features" ref={afterAnim.ref} style={{ padding: '120px 24px', background: '#080910' }}>
        <div className={`reveal ${afterAnim.visible ? 'visible' : ''}`} style={{ maxWidth: 1000, margin: '0 auto' }}>
          <SectionLabel>After Your Pipeline</SectionLabel>
          <SectionTitle sub="Your validated idea becomes a startup workspace with everything you need to build and launch.">
            The pipeline is just the beginning.
          </SectionTitle>

          <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
            {AFTER_FEATURES.map(f => (
              <div key={f.title} className="hover-lift hover-border hover-glow" style={{
                background: CARD, borderRadius: 20, padding: '36px 32px', border: `1px solid ${BORDER}`,
                position: 'relative', overflow: 'hidden',
              }}>
                {/* Corner accent glow */}
                <div style={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', background: `radial-gradient(circle, ${f.color}15, transparent 70%)`, pointerEvents: 'none' }} />
                {/* Free/paid badge */}
                <span style={{
                  position: 'absolute', top: 16, right: 16,
                  fontSize: 11, fontWeight: 600, color: f.badgeColor,
                  background: `${f.badgeColor}14`, border: `1px solid ${f.badgeColor}30`,
                  borderRadius: 20, padding: '3px 10px', letterSpacing: 0.2,
                }}>{f.badge}</span>
                <div style={{ fontSize: 36, marginBottom: 18, position: 'relative' }}>{f.icon}</div>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 10, position: 'relative' }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7, position: 'relative' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          INTELLIGENCE MODULES
      ═══════════════════════════════════════════════════ */}
      <section ref={intelAnim.ref} style={{ padding: '120px 24px', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', top: '20%', right: '-10%', background: `radial-gradient(circle, rgba(6,182,212,0.04) 0%, transparent 60%)` }} />
        </div>
        <div className={`reveal ${intelAnim.visible ? 'visible' : ''}`} style={{ maxWidth: 1000, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <SectionLabel color={CYAN}>Intelligence Suite</SectionLabel>
          <SectionTitle sub="Go deeper with on-demand AI research. Each module gives you competitive intelligence that would take a consultant weeks to compile.">
            5 intelligence modules.<br />Real data, not guesses.
          </SectionTitle>

          <div className="grid-5" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
            {INTEL_MODULES.map(m => (
              <div key={m.title} className="hover-lift hover-border hover-glow" style={{
                background: CARD, borderRadius: 18, padding: '28px 18px', border: `1px solid ${BORDER}`,
                textAlign: 'center', position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', bottom: -20, left: '50%', transform: 'translateX(-50%)', width: 80, height: 80, borderRadius: '50%', background: `radial-gradient(circle, ${m.color}10, transparent 70%)`, pointerEvents: 'none' }} />
                <div style={{ fontSize: 32, marginBottom: 14, position: 'relative' }}>{m.icon}</div>
                <h3 style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 8, position: 'relative' }}>{m.title}</h3>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', lineHeight: 1.65, position: 'relative' }}>{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          COMPARISON — Why not ChatGPT
      ═══════════════════════════════════════════════════ */}
      <section ref={comparisonAnim.ref} style={{ padding: '120px 24px', background: '#080910' }}>
        <div className={`reveal ${comparisonAnim.visible ? 'visible' : ''}`} style={{ maxWidth: 860, margin: '0 auto' }}>
          <SectionLabel>Why StartupScore</SectionLabel>
          <SectionTitle>Not another AI chat.<br />A structured validation engine.</SectionTitle>

          <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Generic AI */}
            <div style={{ background: 'rgba(239,68,68,0.03)', border: '1px solid rgba(239,68,68,0.1)', borderRadius: 20, padding: '32px 28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>💬</div>
                <h3 style={{ color: '#ef4444', fontSize: 16, fontWeight: 700 }}>General-Purpose AI</h3>
              </div>
              {[
                'Generic advice for any question',
                'No structured validation process',
                'No real market data or web research',
                'No dashboard or tracking',
                'Forgets context between sessions',
                'You do all the analysis work',
              ].map(t => (
                <div key={t} style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'flex-start' }}>
                  <span style={{ color: 'rgba(239,68,68,0.5)', flexShrink: 0, fontSize: 16, lineHeight: 1 }}>✗</span>
                  <span style={{ color: 'rgba(255,255,255,0.38)', fontSize: 14, lineHeight: 1.5 }}>{t}</span>
                </div>
              ))}
            </div>

            {/* StartupScore */}
            <div style={{ background: `rgba(124,111,247,0.03)`, border: `1px solid rgba(124,111,247,0.15)`, borderRadius: 20, padding: '32px 28px', animation: 'borderGlow 4s ease-in-out infinite' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: `linear-gradient(135deg, ${P}20, ${A}20)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🚀</div>
                <h3 style={{ color: P, fontSize: 16, fontWeight: 700 }}>StartupScore</h3>
              </div>
              {[
                '5-step pipeline tailored to your profile',
                'Structured 5-step framework with clear outputs',
                'Live web research with real market data',
                'Persistent dashboard per startup',
                '5 intelligence modules for deep research',
                'Actionable outputs: plans, scripts, blueprints',
              ].map(t => (
                <div key={t} style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'flex-start' }}>
                  <span style={{ color: G, flexShrink: 0, fontSize: 16, lineHeight: 1 }}>✓</span>
                  <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, lineHeight: 1.5 }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          EARLY ACCESS — honest social proof
      ═══════════════════════════════════════════════════ */}
      <section style={{ padding: '80px 24px', borderTop: `1px solid ${BORDER}`, background: 'rgba(12,13,18,0.3)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: P, textTransform: 'uppercase', letterSpacing: 3, marginBottom: 16 }}>Early Access</p>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 900, color: '#fff', letterSpacing: -1.5, lineHeight: 1.1, marginBottom: 18 }}>
            Built for founders who move fast.
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7, marginBottom: 40, maxWidth: 540, marginLeft: 'auto', marginRight: 'auto' }}>
            StartupScore is in active beta. Every pipeline run shapes the product. You're not just validating your idea — you're helping define the future of startup intelligence.
          </p>
          <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              { icon: '🔒', text: 'Your idea stays private. Always.' },
              { icon: '⚡', text: 'Real AI. Real web data. No guesses.' },
              { icon: '🛠', text: 'Actively improved every week.' },
            ].map((item, i) => (
              <div key={i} style={{
                background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16,
                padding: '24px 18px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>{item.icon}</div>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, fontWeight: 500 }}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          PRICING
      ═══════════════════════════════════════════════════ */}
      <section id="pricing" ref={pricingAnim.ref} style={{ padding: '120px 24px', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', top: '30%', left: '50%', transform: 'translateX(-50%)', background: `radial-gradient(circle, rgba(124,111,247,0.04) 0%, transparent 60%)` }} />
        </div>
        <div className={`reveal ${pricingAnim.visible ? 'visible' : ''}`} style={{ maxWidth: 1000, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <SectionLabel>Pricing</SectionLabel>
          <SectionTitle sub="Your first pipeline is completely free. After that, buy credit packs — use them for pipelines, intelligence modules, or MVP blueprints.">
            Pay as you validate.
          </SectionTitle>

          {/* Beta Launch Special banner */}
          <div style={{
            background: `linear-gradient(135deg, rgba(249,115,22,0.08), rgba(236,72,153,0.06))`,
            border: `1px solid rgba(249,115,22,0.3)`, borderRadius: 16,
            padding: '16px 24px', marginBottom: 32, textAlign: 'center',
          }}>
            <p style={{ fontSize: 15, fontWeight: 800, color: A }}>
              🚀 Beta Launch Special — <span style={{ color: '#fff' }}>50% off</span> all credit packs
            </p>
          </div>

          {/* Free callout */}
          <div style={{
            background: `linear-gradient(135deg, rgba(124,111,247,0.06), rgba(6,182,212,0.04))`,
            border: `1px solid rgba(124,111,247,0.2)`, borderRadius: 20,
            padding: '24px 32px', marginBottom: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
          }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', marginBottom: 4 }}>Your first pipeline is free</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>All 5 steps. Full AI analysis. No credit card required.</div>
            </div>
            <Btn onClick={() => go('/auth?mode=signup')} size="sm" className="cta-hover">Start Free →</Btn>
          </div>

          <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18, alignItems: 'stretch' }}>
            {PACKS.map(p => (
              <div key={p.id} className={`hover-lift ${p.best ? '' : 'hover-border'}`} style={{
                background: CARD, borderRadius: 22, padding: '32px 24px', position: 'relative',
                border: p.best ? `2px solid ${P}` : `1px solid ${BORDER}`,
                boxShadow: p.best ? `0 0 60px rgba(124,111,247,0.12)` : undefined,
                textAlign: 'center', transform: p.best ? 'scale(1.04)' : undefined,
                overflow: 'hidden',
              }}>
                {/* Best value glow */}
                {p.best && <div style={{ position: 'absolute', top: -40, left: '50%', transform: 'translateX(-50%)', width: 200, height: 100, borderRadius: '50%', background: `radial-gradient(circle, rgba(124,111,247,0.15), transparent 70%)`, pointerEvents: 'none' }} />}
                {p.best && (
                  <div style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    background: `linear-gradient(135deg, ${P}, ${A})`, borderRadius: 20, padding: '4px 16px',
                    fontSize: 10, fontWeight: 700, color: '#fff', letterSpacing: 1, textTransform: 'uppercase', whiteSpace: 'nowrap',
                  }}>{p.tag}</div>
                )}
                <h3 style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 4, marginTop: p.best ? 4 : 0, position: 'relative' }}>{p.label}</h3>
                {!p.best && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>{p.tag}</p>}
                {p.best && <div style={{ height: 16 }} />}
                <div style={{ position: 'relative', marginBottom: 2 }}>
                  <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.3)', textDecoration: 'line-through', marginBottom: 2 }}>
                    ${p.price.toFixed(2)}
                  </div>
                  <div style={{ fontSize: 42, fontWeight: 900, color: A }}>
                    ${p.betaPrice.toFixed(2)}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 20 }}>{p.credits} credits</div>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginBottom: 24, minHeight: 32, position: 'relative' }}>{p.hint}</p>
                <button onClick={() => go(`/auth?mode=signup&plan=${p.id.toLowerCase()}`)} className="cta-hover" style={{
                  width: '100%', padding: '14px 0', borderRadius: 12, fontSize: 14, fontWeight: 700,
                  background: p.best ? `linear-gradient(135deg, ${P}, ${A})` : 'rgba(255,255,255,0.04)',
                  border: p.best ? 'none' : `1px solid ${BORDER}`, color: '#fff', position: 'relative',
                }}>
                  Get {p.label}
                </button>
              </div>
            ))}

          </div>

          {/* Credit system explainer */}
          <div style={{
            background: 'rgba(255,255,255,0.02)', border: `1px solid ${BORDER}`,
            borderRadius: 14, padding: '16px 24px', marginTop: 28, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto',
          }}>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7, textAlign: 'center' }}>
              <strong style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>How credits work:</strong> One full pipeline costs ~16 credits. Intelligence modules cost 1–3 credits each. Your 25 free credits cover one complete pipeline run plus a few modules to go deeper.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          FAQ
      ═══════════════════════════════════════════════════ */}
      <section id="faq" ref={faqAnim.ref} style={{ padding: '120px 24px', background: '#080910' }}>
        <div className={`reveal ${faqAnim.visible ? 'visible' : ''}`} style={{ maxWidth: 700, margin: '0 auto' }}>
          <SectionLabel>FAQ</SectionLabel>
          <SectionTitle>Common questions</SectionTitle>

          {[
            { q: 'Is the first pipeline really free?', a: 'Yes. All 5 steps of your first pipeline are completely free — no credit card required. You get the full AI analysis including founder profiling, idea scoring, reality check, positioning, and a validation plan.' },
            { q: 'How is this different from ChatGPT?', a: 'ChatGPT gives generic answers. StartupScore runs a structured 5-step pipeline with live web research, market data, and competitive intelligence. Every insight is personalized to your founder profile and specific idea. Plus you get a persistent dashboard, financial modeling, and 5 intelligence modules.' },
            { q: 'How long does the pipeline take?', a: 'Most founders complete all 5 steps in 25-35 minutes. Each step generates AI analysis in real-time as you go.' },
            { q: 'Is my idea kept confidential?', a: 'Yes. Your ideas are never shared, sold, or used to train models. Your data is encrypted at rest and in transit.' },
            { q: 'Can I validate multiple ideas?', a: 'Absolutely. You start with 25 free credits — enough for a full pipeline plus some intelligence modules. Need more? The Starter pack (100 credits, $4.95 during beta) covers about 6 full pipelines.' },
            { q: 'What are intelligence modules?', a: 'After your pipeline, you can go deeper with 5 on-demand research modules: Name Validation, Keyword Intelligence, Competitor Intelligence, Trends & Timing, and Sentiment Analysis. Each costs 1-3 credits and gives you research that would take a consultant weeks.' },
            { q: 'What happens after the pipeline?', a: 'Your validated idea becomes a startup workspace with a dashboard, milestone tracking, financial modeler, MVP blueprint generation, and the ability to share investor-ready reports.' },
            { q: 'Can I export or share my results?', a: 'Yes. Every startup dashboard has shareable links. Several founders have used their validation data in investor conversations.' },
          ].map(({ q, a }, i) => (
            <FAQItem key={i} q={q} a={a} isOpen={openFAQ === i} onClick={() => setOpenFAQ(openFAQ === i ? null : i)} />
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          FINAL CTA
      ═══════════════════════════════════════════════════ */}
      <section ref={ctaAnim.ref} style={{ padding: '160px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        {/* Multiple gradient orbs */}
        <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, rgba(124,111,247,0.08) 0%, transparent 60%)`, top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: `radial-gradient(circle, rgba(249,115,22,0.06) 0%, transparent 60%)`, top: '30%', left: '30%', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: `radial-gradient(circle, rgba(6,182,212,0.04) 0%, transparent 60%)`, bottom: '20%', right: '30%', pointerEvents: 'none' }} />

        <div className={`reveal ${ctaAnim.visible ? 'visible' : ''}`} style={{ maxWidth: 620, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Decorative rings */}
          <div style={{ position: 'absolute', top: -60, left: '50%', transform: 'translateX(-50%)', width: 200, height: 200, borderRadius: '50%', border: `1px solid rgba(124,111,247,0.06)`, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 280, height: 280, borderRadius: '50%', border: `1px solid rgba(124,111,247,0.03)`, pointerEvents: 'none' }} />

          <h2 style={{ fontSize: 'clamp(32px, 5vw, 60px)', fontWeight: 900, color: '#fff', letterSpacing: -2.5, lineHeight: 1.06, marginBottom: 20 }}>
            Your idea deserves<br />
            <span style={{
              background: `linear-gradient(135deg, ${P}, ${CYAN})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>a real answer.</span>
          </h2>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.38)', marginBottom: 48, lineHeight: 1.65 }}>
            5 AI-powered steps. Under 30 minutes. Completely free to start.
          </p>

          <Btn onClick={() => go('/auth?mode=signup')} size="lg" pulse className="cta-hover">
            Start My Pipeline — Free
          </Btn>

          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.18)', marginTop: 22 }}>
            No credit card · No pitch deck · Your data stays private
          </p>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.12)', marginTop: 10 }}>
            In beta · Actively developed · Your feedback shapes the roadmap
          </p>
        </div>
      </section>

      {/* ─ Mobile sticky CTA ─ */}
      <div className="mobile-cta" style={{
        display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 997,
        background: 'rgba(5,6,8,0.95)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        borderTop: `1px solid ${BORDER}`, padding: '10px 16px', alignItems: 'center', justifyContent: 'center', gap: 12,
      }}>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>25 free credits</span>
        <Btn onClick={() => go('/auth?mode=signup')} size="sm" className="cta-hover">Start Free</Btn>
      </div>

      {/* ─ Footer ─ */}
      <footer style={{ background: '#030406', padding: '52px 24px 36px', borderTop: `1px solid ${BORDER}` }}>
        <div className="footer-grid" style={{ maxWidth: 880, margin: '0 auto', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 40 }}>
          <div>
            <LogoLockup size="md" />
            <p style={{ color: 'rgba(255,255,255,0.22)', fontSize: 13, lineHeight: 1.6, marginTop: 12, maxWidth: 280 }}>
              AI-powered startup validation. From raw idea to investor-ready startup in 30 minutes.
            </p>
            <p style={{ color: 'rgba(255,255,255,0.12)', fontSize: 12, marginTop: 16 }}>© 2026 StartupScore.live</p>
          </div>
          <div>
            <h4 style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Product</h4>
            {['Pipeline', 'Features', 'Pricing', 'FAQ'].map(l => (
              <button key={l} onClick={() => scrollTo(l.toLowerCase())} style={{ display: 'block', background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', fontSize: 13, padding: '5px 0', marginBottom: 4, transition: 'color 0.2s' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.25)')}
              >{l}</button>
            ))}
          </div>
          <div>
            <h4 style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Legal</h4>
            {[
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Contact', href: '/contact' },
            ].map(({ label, href }) => (
              <a key={label} href={href} style={{ display: 'block', color: 'rgba(255,255,255,0.25)', fontSize: 13, textDecoration: 'none', padding: '5px 0', marginBottom: 4, transition: 'color 0.2s' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.25)')}
              >{label}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
